import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, LogOut } from 'lucide-react';
import { auth } from '@/lib/api';
import { visibleItems } from './navigation.config';
import useActiveRoute from './useActiveRoute';
import useReadinessSignal from '@/hooks/useReadinessSignal';

const LEVEL_COLOR = {
  green: 'var(--accent-green, #00C853)',
  amber: 'var(--accent-amber, #FFB000)',
  red:   'var(--accent-red, #FF3B30)',
};

/**
 * MobileMoreDrawer — bottom sheet listing every "more" item plus
 * Logout. Mounted once at AppShell scope so it works on every route.
 *
 * Closes on: backdrop click · Escape · route navigation. Implements
 * a soft focus trap and locks body scroll while open.
 */
export default function MobileMoreDrawer({ open, onClose }) {
  const navigate = useNavigate();
  const isActive = useActiveRoute();
  const user = auth.currentUser();
  const role = user?.role || 'operator';
  const items = visibleItems(role, 'more');
  const panelRef = useRef(null);
  const readiness = useReadinessSignal();

  // Esc to close + body scroll lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // soft focus
    setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const logout = () => { onClose(); auth.logout(); navigate('/login'); };

  return (
    <div
      data-testid="mobile-more-drawer"
      role="dialog"
      aria-modal="true"
      aria-label="More navigation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 9700,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end',
      }}>
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          background: '#0a0a0a',
          borderTop: '1px solid var(--border)',
          maxHeight: '85vh', overflowY: 'auto',
          padding: '12px 0',
          paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0))',
        }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0 16px 12px',
                      borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontSize: 11, letterSpacing: '0.08em',
                         color: 'var(--text-secondary)' }}>
            MORE NAVIGATION
          </span>
          <button onClick={onClose}
                  data-testid="mobile-more-close"
                  aria-label="Close more menu"
                  style={{ background: 'transparent', border: 'none',
                           color: 'var(--text-primary)',
                           cursor: 'pointer', padding: 8 }}>
            <X size={18} />
          </button>
        </div>

        {items.map(({ id, to, label, Icon, testId }) => {
          const active = isActive(to);
          return (
            <Link
              key={id}
              to={to}
              data-testid={`mobile-more-${id}`}
              onClick={onClose}
              aria-current={active ? 'page' : undefined}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 16px', minHeight: 44,
                fontSize: 13, letterSpacing: '0.06em',
                color: active
                  ? 'var(--accent-amber)' : 'var(--text-primary)',
                textDecoration: 'none',
                borderBottom: '1px solid var(--border)',
                background: active ? '#1a1a1a' : 'transparent',
              }}>
              <Icon size={16}
                    style={{ color: 'var(--accent-amber)' }} />
              {label}
              {id === 'promotion-console'
                && readiness.level !== 'hidden' && (
                <span data-testid="mobile-readiness-dot"
                      data-level={readiness.level}
                      title={`${readiness.readiness_pct}% ready`}
                      style={{
                        display: 'inline-block',
                        width: 8, height: 8,
                        borderRadius: '50%',
                        background: LEVEL_COLOR[readiness.level]
                          || LEVEL_COLOR.red,
                        boxShadow: `0 0 6px `
                          + `${LEVEL_COLOR[readiness.level]
                            || LEVEL_COLOR.red}`,
                        marginLeft: 8,
                      }} />
              )}
            </Link>
          );
        })}

        <button
          onClick={logout}
          data-testid="mobile-more-logout"
          style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '16px', minHeight: 44, width: '100%',
            background: 'transparent', border: 'none',
            cursor: 'pointer', textAlign: 'left',
            fontSize: 13, letterSpacing: '0.06em',
            color: 'var(--accent-red, #ef4444)',
          }}>
          <LogOut size={16} />
          LOGOUT
        </button>
      </div>
    </div>
  );
}
